import { useState, useEffect } from 'react';
import { useRegisterMutation } from '../slices/usersApiSlice';
import { showToast } from '../utils/toast';
import Loader from '../components/Loader';
import AdminUserCreate from './AdminUserCreate';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import { motion } from 'framer-motion';
import { FaTrash, FaSearch, FaSync, FaUsers, FaGraduationCap, FaSchool, FaBook, FaUserCog, FaUserShield, FaTachometerAlt, FaServer, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import config from '../config/config';

const AdminDashboard = () => {
  const { isDarkMode } = useTheme();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState('student');
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState({
    totalUsers: 0,
    students: 0,
    faculty: 0,
    institutes: 0,
    totalExams: 0,
    activeExams: 0,
    totalPapers: 0,
    departments: 0,
    totalUsersGrowth: 0,
    studentGrowth: 0,
    facultyGrowth: 0,
    instituteGrowth: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminComment, setAdminComment] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('adminDashboardTab') || 'overview';
  });
  const [processingType, setProcessingType] = useState(null);
  const [users, setUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userError, setUserError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [recentActivities, setRecentActivities] = useState([]);
  const [systemHealth, setSystemHealth] = useState({
    status: 'loading',
    uptime: '-',
    apiResponseTime: '-',
    memory: {
      used: '-',
      total: '-',
      percentage: '-'
    },
    database: {
      status: '-',
      connections: '-'
    },
    lastChecked: new Date().toISOString()
  });

  const [register, { isLoading }] = useRegisterMutation();

  // Add backend URL
  const BACKEND_URL = 'https://nexusedu-a7gk.onrender.com';

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch dashboard statistics
      const statsRes = await axios.get(`${BACKEND_URL}/api/admin/dashboard`, { withCredentials: true });
      
      // Process stats data
      setStats({
        totalUsers: statsRes.data.totalUsers || 0,
        students: statsRes.data.studentCount || 0,
        faculty: statsRes.data.facultyCount || 0,
        institutes: statsRes.data.instituteCount || 0,
        totalExams: statsRes.data.totalExams || 0,
        activeExams: statsRes.data.activeExams || 0,
        totalPapers: statsRes.data.totalPapers || 0,
        departments: statsRes.data.departmentCount || 0,
        totalUsersGrowth: statsRes.data.totalUsersGrowth || 0,
        studentGrowth: statsRes.data.studentGrowth || 0,
        facultyGrowth: statsRes.data.facultyGrowth || 0,
        instituteGrowth: statsRes.data.instituteGrowth || 0
      });
      
      // Fetch recent activities
      try {
        const activitiesRes = await axios.get(`${BACKEND_URL}/api/admin/activities`, { withCredentials: true });
        setRecentActivities(activitiesRes.data || []);
      } catch (activityError) {
        console.error('Error fetching activities:', activityError);
        // If activities endpoint fails, keep any existing activities data
      }
      
      // Fetch system health data
      fetchSystemHealth();
      
      setError(null);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch system health data
  const fetchSystemHealth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/system-health`, { 
        withCredentials: true 
      });
      
      if (response.data) {
        setSystemHealth(response.data);
      }
    } catch (error) {
      console.error('Error fetching system health:', error);
      setSystemHealth(prev => ({
        ...prev,
        status: 'issues',
        error: 'Failed to fetch system health data'
      }));
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const submitHandler = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/users`,
        {
          name,
          email,
          userType
        },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.data.success) {
        showToast.success(`Successfully created ${userType} account. Credentials sent to user's email.`);
        
        // Reset form
        setName('');
        setEmail('');
        setUserType('student'); 
        
        // Fetch updated user list
        await fetchUsers();

        // Switch to users tab
        setActiveTab('users');
      }
    } catch (err) {
      showToast.error(err.response?.data?.message || 'Error creating user');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setAdminComment('Approved by admin');
    setShowModal(true);
    setProcessingType('approve');
  };

  const handleReject = (request) => {
    setSelectedRequest(request);
    setAdminComment('');
    setShowModal(true);
    setProcessingType('reject');
  };

  const handleReviewPaper = async (request) => {
    setActionLoading(true);
    try {
      // Fetch paper details for review
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/requests/review/${request._id}`,
        { withCredentials: true }
      );
      
      setReviewData(response.data);
      setSelectedRequest(request);
      setShowReviewModal(true);
    } catch (error) {
      console.error('Error fetching paper details:', error);
      showToast.error('Failed to load paper details for review');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle request status update (for exam requests)
  const handleRequestStatusUpdate = async () => {  
    if (!selectedRequest) return;

    setActionLoading(true);
    const status = processingType === 'approve' ? 'approved' : 'rejected';
    setProcessingStatus(status === 'approved' ? 'Encrypting and uploading to IPFS...' : 'Processing request...');

    try {
      const response = await axios.put(
        `${BACKEND_URL}/api/admin/requests/${selectedRequest._id}`,
        {
          status,
          adminComment
        },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );

      if (response.data) {
        setRequests(requests.map(req =>
          req._id === selectedRequest._id
            ? {
                ...req,
                status: response.data.status,
                ipfsHash: response.data.ipfsHash,
                ipfsEncryptionKey: response.data.ipfsEncryptionKey
              }
            : req
        ));

        if (status === 'approved' && response.data.ipfsHash) {
          showToast.success(`Request approved and uploaded to IPFS\nHash: ${response.data.ipfsHash.slice(0, 10)}...`);
        } else {
          showToast.success(`Request ${status} successfully`);
        }

        await fetchData();
        setShowModal(false);
        setSelectedRequest(null);
        setAdminComment('');
        setProcessingType(null);
      }
    } catch (error) {
      console.error('Status update error:', error);
      showToast.error(error.response?.data?.message || `Failed to ${status} request`);
    } finally {
      setActionLoading(false);
      setProcessingStatus('');
    }
  };

  // Fetch users
  const fetchUsers = async () => {
    try {
      setUserLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/admin/users`, {
        withCredentials: true
      });
      
      if (response.data) {
        setUsers(response.data);
        setFilteredUsers(response.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      showToast.error('Failed to fetch users');
      setUserError('Failed to fetch users');
    } finally {
      setUserLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    }
  }, [activeTab]);

  useEffect(() => {
    if (users) {
      setFilteredUsers(
        users.filter(user => 
          user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.userType.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
  }, [users, searchTerm]);

  // Handle user deletion
  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await axios.delete(
          `${BACKEND_URL}/api/admin/users/${userId}`,
          { withCredentials: true }
        );
        fetchUsers(); // Refresh user list
        showToast.success('User deleted successfully');
      } catch (error) {
        showToast.error('Failed to delete user');
      }
    }
  };

  // Add refresh function
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchData();
      await fetchSystemHealth();
      showToast.success('Data refreshed successfully');
    } catch (error) {
      showToast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Add this function near your other handlers
  const handleUserListRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchUsers();
      showToast.success('User list refreshed successfully');
    } catch (error) {
      showToast.error('Failed to refresh user list');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Update tab handler to save to localStorage
  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    localStorage.setItem('adminDashboardTab', tab);
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return 'Unknown date';
    }
    
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      return new Intl.DateTimeFormat('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid date';
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader fullScreen text="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div className={`min-h-screen pt-20 ${isDarkMode ? 'bg-[#0A0F1C]' : 'bg-gray-50'}`}>
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6 relative z-0">
          <div className={`flex flex-wrap gap-2 sm:gap-4 border-b transition-none ${
            isDarkMode ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <button
              onClick={() => handleTabSwitch('overview')}
              className={`px-3 sm:px-4 py-2 font-medium transition-none text-sm sm:text-base flex items-center gap-2 ${
                activeTab === 'overview'
                  ? isDarkMode
                    ? 'text-violet-400 border-b-2 border-violet-400'
                    : 'text-violet-600 border-b-2 border-violet-600'
                  : isDarkMode
                  ? 'text-gray-400 hover:text-gray-300'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <FaTachometerAlt className="h-4 w-4" />
              Dashboard
            </button>
            <button
              onClick={() => handleTabSwitch('users')}
              className={`px-3 sm:px-4 py-2 font-medium transition-none text-sm sm:text-base flex items-center gap-2 ${
                activeTab === 'users'
                  ? isDarkMode
                    ? 'text-violet-400 border-b-2 border-violet-400'
                    : 'text-violet-600 border-b-2 border-violet-600'
                  : isDarkMode
                  ? 'text-gray-400 hover:text-gray-300'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <FaUserCog className="h-4 w-4" />
              Manage Users
            </button>
          </div>
        </div>

        {activeTab === 'overview' ? (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className={`rounded-lg shadow-md overflow-hidden ${
                isDarkMode ? 'bg-[#1a1f2e]' : 'bg-white'
              }`}>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Users</p>
                      <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{stats.totalUsers}</h3>
                    </div>
                    <div className={`p-3 rounded-full ${isDarkMode ? 'bg-indigo-900/20' : 'bg-indigo-100'}`}>
                      <FaUsers className={`h-6 w-6 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-xs font-medium">
                      <span className={`${
                        stats.totalUsersGrowth >= 0 
                          ? (isDarkMode ? 'text-green-400' : 'text-green-600') 
                          : (isDarkMode ? 'text-red-400' : 'text-red-600')
                      }`}>
                        {stats.totalUsersGrowth >= 0 ? '+' : ''}{stats.totalUsersGrowth}%
                      </span>
                      <span className={`ml-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>from last month</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className={`rounded-lg shadow-md overflow-hidden ${
                isDarkMode ? 'bg-[#1a1f2e]' : 'bg-white'
              }`}>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Students</p>
                      <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{stats.students}</h3>
                    </div>
                    <div className={`p-3 rounded-full ${isDarkMode ? 'bg-green-900/20' : 'bg-green-100'}`}>
                      <FaGraduationCap className={`h-6 w-6 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-xs font-medium">
                      <span className={`${
                        stats.studentGrowth >= 0 
                          ? (isDarkMode ? 'text-green-400' : 'text-green-600') 
                          : (isDarkMode ? 'text-red-400' : 'text-red-600')
                      }`}>
                        {stats.studentGrowth >= 0 ? '+' : ''}{stats.studentGrowth}%
                      </span>
                      <span className={`ml-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>from last month</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className={`rounded-lg shadow-md overflow-hidden ${
                isDarkMode ? 'bg-[#1a1f2e]' : 'bg-white'
              }`}>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Faculty</p>
                      <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{stats.faculty}</h3>
                    </div>
                    <div className={`p-3 rounded-full ${isDarkMode ? 'bg-blue-900/20' : 'bg-blue-100'}`}>
                      <FaUserShield className={`h-6 w-6 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-xs font-medium">
                      <span className={`${
                        stats.facultyGrowth >= 0 
                          ? (isDarkMode ? 'text-green-400' : 'text-green-600') 
                          : (isDarkMode ? 'text-red-400' : 'text-red-600')
                      }`}>
                        {stats.facultyGrowth >= 0 ? '+' : ''}{stats.facultyGrowth}%
                      </span>
                      <span className={`ml-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>from last month</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className={`rounded-lg shadow-md overflow-hidden ${
                isDarkMode ? 'bg-[#1a1f2e]' : 'bg-white'
              }`}>
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Institutes</p>
                      <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{stats.institutes}</h3>
                    </div>
                    <div className={`p-3 rounded-full ${isDarkMode ? 'bg-yellow-900/20' : 'bg-yellow-100'}`}>
                      <FaSchool className={`h-6 w-6 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-xs font-medium">
                      <span className={`${
                        stats.instituteGrowth >= 0 
                          ? (isDarkMode ? 'text-green-400' : 'text-green-600') 
                          : (isDarkMode ? 'text-red-400' : 'text-red-600')
                      }`}>
                        {stats.instituteGrowth >= 0 ? '+' : ''}{stats.instituteGrowth}%
                      </span>
                      <span className={`ml-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>from last month</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* System Health Section */}
            <div className={`rounded-lg shadow-md overflow-hidden ${
              isDarkMode ? 'bg-[#1a1f2e]' : 'bg-white'
            }`}>
              <div className="p-5 border-b flex justify-between items-center">
                <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>System Health</h2>
                <button
                  onClick={fetchSystemHealth}
                  disabled={isRefreshing}
                  className={`p-2 rounded-lg transition ${
                    isDarkMode 
                      ? 'bg-[#2a2f3e] hover:bg-[#3a3f4e] text-gray-300' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                  } ${isRefreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title="Refresh system health"
                >
                  <FaSync className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#2a2f3e]' : 'bg-gray-50'}`}>
                    <div className="flex items-center">
                      <div className={`p-3 rounded-full mr-4 ${
                        systemHealth.status === 'operational'
                          ? (isDarkMode ? 'bg-green-900/20' : 'bg-green-100')
                          : systemHealth.status === 'loading'
                          ? (isDarkMode ? 'bg-blue-900/20' : 'bg-blue-100')
                          : (isDarkMode ? 'bg-red-900/20' : 'bg-red-100')
                      }`}>
                        {systemHealth.status === 'operational' 
                          ? <FaCheckCircle className={`h-5 w-5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                          : systemHealth.status === 'loading'
                          ? <FaSync className={`h-5 w-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'} animate-spin`} />
                          : <FaExclamationTriangle className={`h-5 w-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                        }
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>System Status</p>
                        <p className={`text-lg font-bold ${
                          systemHealth.status === 'operational'
                            ? (isDarkMode ? 'text-green-400' : 'text-green-600')
                            : systemHealth.status === 'loading'
                            ? (isDarkMode ? 'text-blue-400' : 'text-blue-600')
                            : (isDarkMode ? 'text-red-400' : 'text-red-600')
                        }`}>
                          {systemHealth.status === 'operational' ? 'Operational' : 
                           systemHealth.status === 'loading' ? 'Loading...' : 'Issues Detected'}
                        </p>
                      </div>
                    </div>
                    {systemHealth.database && (
                      <div className="mt-2">
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Database: {systemHealth.database.status}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#2a2f3e]' : 'bg-gray-50'}`}>
                    <div className="flex items-center">
                      <div className={`p-3 rounded-full mr-4 ${isDarkMode ? 'bg-blue-900/20' : 'bg-blue-100'}`}>
                        <FaServer className={`h-5 w-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Uptime</p>
                        <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{systemHealth.uptime}</p>
                      </div>
                    </div>
                    {systemHealth.memory && (
                      <div className="mt-2">
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Heap: {systemHealth.memory.heapUsed}/{systemHealth.memory.heapTotal} ({systemHealth.memory.heapPercentage})
                        </p>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          RSS: {systemHealth.memory.rss}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#2a2f3e]' : 'bg-gray-50'}`}>
                    <div className="flex items-center">
                      <div className={`p-3 rounded-full mr-4 ${isDarkMode ? 'bg-purple-900/20' : 'bg-purple-100'}`}>
                        <FaSync className={`h-5 w-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>API Response</p>
                        <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{systemHealth.apiResponseTime}</p>
                      </div>
                    </div>
                    {/* {systemHealth.database && (
                      <div className="mt-2">
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          DB Connections: {systemHealth.database.connections}
                        </p>
                      </div>
                    )} */}
                  </div>
                  
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-[#2a2f3e]' : 'bg-gray-50'}`}>
                    <div className="flex items-center">
                      <div className={`p-3 rounded-full mr-4 ${isDarkMode ? 'bg-amber-900/20' : 'bg-amber-100'}`}>
                        <FaTachometerAlt className={`h-5 w-5 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Last Checked</p>
                        <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {formatDate(systemHealth.lastChecked).replace('at', '')}
                        </p>
                      </div>
                    </div>
                    {systemHealth.errors && systemHealth.errors.length > 0 && (
                      <div className="mt-2">
                        <p className={`text-xs text-red-500`}>
                          {systemHealth.errors[0]}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activities and Quick Actions Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Recent Activities */}
              <div className={`lg:col-span-3 rounded-lg shadow-md overflow-hidden ${
                isDarkMode ? 'bg-[#1a1f2e]' : 'bg-white'
              }`}>
                <div className="flex justify-between items-center p-5 border-b">
                  <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Recent Activities</h2>
                  <button
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    className={`p-2 rounded-lg transition ${
                      isDarkMode 
                        ? 'bg-[#2a2f3e] hover:bg-[#3a3f4e] text-gray-300' 
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                    } ${isRefreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <FaSync className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                <div className="p-5">
                  {recentActivities.length === 0 ? (
                    <div className="text-center py-8">
                      <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>No recent activities found</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {recentActivities.map((activity) => (
                        <div key={activity.id} className={`p-4 rounded-lg ${
                          isDarkMode ? 'bg-[#2a2f3e]' : 'bg-gray-50'
                        }`}>
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="flex items-center">
                                <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                                  activity.action.includes('created') 
                                    ? 'bg-green-500' 
                                    : activity.action.includes('deleted')
                                    ? 'bg-red-500'
                                    : activity.action.includes('approved')
                                    ? 'bg-blue-500'
                                    : activity.action.includes('rejected')
                                    ? 'bg-yellow-500'
                                    : 'bg-gray-500'
                                }`}></span>
                                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{activity.action}</p>
                              </div>
                              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>By {activity.user}</p>
                              {activity.details && (
                                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {activity.details}
                                </p>
                              )}
                            </div>
                            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {formatDate(activity.timestamp)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 h-auto lg:h-[calc(100vh-200px)]">
            {/* Create User Form - Fixed width */}
            <div className="w-full lg:w-[400px] flex-shrink-0 h-full">
              <div className={`rounded-lg shadow-md ${
                isDarkMode ? 'bg-[#1a1f2e]' : 'bg-white'
              } h-full`}>
                <div className="p-3 sm:p-4 lg:p-6 h-full">
                  <h2 className={`text-lg sm:text-xl font-semibold mb-4 sm:mb-6 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>Create New User</h2>
                  <AdminUserCreate onUserCreated={fetchUsers} />
                </div>
              </div>
            </div>

            {/* Users Table - Fixed height and scrollable */}
            <div className="flex-1 h-full min-w-0">
              <div className={`rounded-lg shadow-md ${
                isDarkMode ? 'bg-[#1a1f2e]' : 'bg-white'
              } h-full flex flex-col`}>
                <div className="p-4 lg:p-6 flex flex-col h-[calc(100vh-200px)] sm:h-full">
                  {/* Header section */}
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div className="flex items-center gap-3 min-w-0">
                      <h2 className={`text-xl font-semibold ${
                        isDarkMode ? 'text-white' : 'text-gray-900'
                      }`}>
                        User List
                      </h2>
                      <button
                        onClick={handleUserListRefresh}
                        disabled={isRefreshing}
                        className={`p-2 rounded-lg transition-all duration-200 flex-shrink-0 ${
                          isDarkMode 
                            ? 'bg-[#2a2f3e] hover:bg-[#3a3f4e] text-gray-300' 
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                        } ${isRefreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title="Refresh user list"
                      >
                        <FaSync className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                      </button>
                    </div>
                    {/* Search input */}
                    <div className="relative w-full sm:w-auto flex-shrink-0">
                      <input
                        type="text"
                        placeholder="Search by email or type..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={`pl-10 pr-4 py-2 rounded-lg w-full sm:w-72 ${
                          isDarkMode 
                            ? 'bg-[#2a2f3e] text-white border-gray-700' 
                            : 'bg-white text-gray-900 border-gray-300'
                        } border focus:outline-none focus:ring-1 focus:ring-violet-500`}
                      />
                      <FaSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`} />
                    </div>
                  </div>

                  {/* Table container with fixed height and scrolling */}
                  <div className="flex-1 overflow-hidden h-full -mx-4 sm:mx-0">
                    {userLoading ? (
                      <div className="flex justify-center items-center h-full">
                        <Loader />
                      </div>
                    ) : userError ? (
                      <div className="text-red-500 p-4">{userError}</div>
                    ) : (
                      <div className="h-full overflow-auto">
                        <div className="inline-block min-w-full align-middle">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className={`${isDarkMode ? 'bg-[#1a1f2e]' : 'bg-white'} sticky top-0 z-10`}>
                              <tr>
                                {['Name', 'Email', 'Type', 'Status', 'Actions'].map((header) => (
                                  <th 
                                    key={header} 
                                    className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${
                                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                                    }`}
                                  >
                                    {header}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className={`divide-y ${
                              isDarkMode ? 'divide-gray-700' : 'divide-gray-200'
                            }`}>
                              {filteredUsers.map((user) => (
                                <tr key={user._id} className={`${
                                  isDarkMode 
                                    ? 'hover:bg-[#2a2f3e] text-gray-200' 
                                    : 'hover:bg-gray-50 text-gray-900'
                                }`}>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm">{user.name}</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm">{user.email}</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm capitalize">{user.userType}</td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                      user.isActive
                                        ? 'bg-green-500 text-white'
                                        : 'bg-red-500 text-white'
                                    }`}>
                                      {user.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <button
                                      onClick={() => handleDeleteUser(user._id)}
                                      className={`p-1.5 rounded-full ${
                                        isDarkMode 
                                          ? 'hover:bg-[#3a3f4e] text-red-400' 
                                          : 'hover:bg-gray-100 text-red-500'
                                      }`}
                                    >
                                      <FaTrash className="w-4 h-4" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
              {/* Overlay */}
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className={`absolute inset-0 ${isDarkMode ? 'bg-black' : 'bg-gray-500'} opacity-75`}></div>
              </div>

              {/* Modal */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`${
                  isDarkMode 
                    ? 'bg-[#1a1f2e] text-white border border-gray-700' 
                    : 'bg-white text-gray-900 border border-gray-300'
                } rounded-lg w-[95%] sm:max-w-md shadow-xl relative mx-auto`}
              >
                <div className={`flex justify-between items-center p-6 border-b ${
                  isDarkMode ? 'border-gray-700' : 'border-gray-300'
                }`}>
                  <h3 className="text-lg font-medium">
                    {processingType === 'approve' ? 'Approve' : 'Reject'} Request
                  </h3>
                  {!actionLoading && (
                    <button
                      onClick={() => setShowModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <span className="sr-only">Close</span>
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>

                <div className="p-6">
                  <div className="mb-4">
                    <label className={`block text-sm font-medium mb-1 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Admin Comment
                    </label>
                    <textarea
                      rows={3}
                      value={adminComment}
                      onChange={(e) => setAdminComment(e.target.value)}
                      disabled={actionLoading}
                      className={`w-full px-3 py-2 rounded-lg shadow-sm text-sm sm:text-base ${
                        isDarkMode 
                          ? 'bg-[#2a2f3e] border-gray-700 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                  </div>

                  {actionLoading && (
                    <div className="flex items-center justify-center space-x-2">
                      <Loader size="small" text={processingStatus} />
                    </div>
                  )}
                </div>

                <div className={`px-6 py-4 flex justify-end space-x-3 ${
                  isDarkMode ? 'bg-[#2a2f3e]' : 'bg-gray-50'
                }`}>
                  <button
                    onClick={() => setShowModal(false)}
                    disabled={actionLoading}
                    className={`px-4 py-2 rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRequestStatusUpdate}
                    disabled={actionLoading}
                    className={`px-4 py-2 rounded-lg ${
                      isDarkMode 
                        ? 'bg-violet-600 text-white hover:bg-violet-700' 
                        : 'bg-violet-500 text-white hover:bg-violet-600'
                    }`}
                  >
                    {processingType === 'approve' ? 'Approve' : 'Reject'}
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        )}

        {/* Review Paper Modal */}
        {showReviewModal && reviewData && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
              {/* Overlay */}
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className={`absolute inset-0 ${isDarkMode ? 'bg-black' : 'bg-gray-500'} opacity-75`}></div>
              </div>

              {/* Modal */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`${
                  isDarkMode 
                    ? 'bg-[#1a1f2e] text-white border border-gray-700' 
                    : 'bg-white text-gray-900 border border-gray-300'
                } rounded-lg w-full sm:max-w-4xl shadow-xl relative mx-auto max-h-[90vh] overflow-y-auto`}
              >
                <div className={`flex justify-between items-center p-6 border-b ${
                  isDarkMode ? 'border-gray-700' : 'border-gray-300'
                } sticky top-0 bg-inherit z-10`}>
                  <h3 className="text-lg font-medium">
                    Reviewing Paper: {selectedRequest?.examName}
                  </h3>
                  <button
                    onClick={() => setShowReviewModal(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="p-6">
                  {/* Paper details */}
                  <div className="mb-8">
                    <div className={`p-6 rounded-lg ${
                      isDarkMode ? 'bg-[#2a2f3e]' : 'bg-gray-50'
                    }`}>
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <h4 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                            Exam Information
                          </h4>
                          <div className="space-y-3">
                            <div>
                              <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Exam Name</p>
                              <p className={`text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{reviewData.examName}</p>
                            </div>
                            <div>
                              <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Subject</p>
                              <p className={`text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{reviewData.subject?.name || "Not specified"}</p>
                            </div>
                            <div>
                              <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Questions</p>
                              <p className={`text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{reviewData.totalQuestions}</p>
                            </div>
                          </div>
                        </div>
                        <div> 
                          <h4 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                            Submission Details
                          </h4>
                          <div className="space-y-3">
                            <div>
                              <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Institute</p>
                              <p className={`text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{reviewData.institute?.name}</p>
                            </div>
                            <div>
                              <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Passing Percentage</p>
                              <p className={`text-base ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{reviewData.passingPercentage || 60}%</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Questions list */}
                  <div className="mb-6">
                    <h4 className={`text-xl font-semibold mb-6 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      Question Paper Preview
                    </h4>
                    <div className={`space-y-8`}>
                      {reviewData.questions?.map((question, index) => (
                        <div key={index} className={`p-6 rounded-lg ${
                          isDarkMode ? 'bg-[#2a2f3e]' : 'bg-gray-50'
                        }`}>
                          <div className="flex flex-col space-y-4">
                            {/* Question Header */}
                            <div className="flex items-start">
                              <span className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full mr-3 ${
                                isDarkMode ? 'bg-violet-500/20 text-violet-400' : 'bg-violet-100 text-violet-600'
                              }`}>
                                {index + 1}
                              </span>
                              <div className="flex-grow">
                                <p className={`text-lg font-medium ${
                                  isDarkMode ? 'text-white' : 'text-gray-900'
                                }`}>
                                  {question.text || question.question || "No question text available"}
                                </p>
                                {(question.questionImage || question.image) && (
                                  <div className="mt-4">
                                    <img 
                                      src={question.questionImage || question.image} 
                                      alt={`Question ${index + 1} image`}
                                      className="max-w-full rounded-lg max-h-64 object-contain border shadow-sm" 
                                    />
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Options */}
                            <div className="ml-11 mt-2">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {question.options?.map((option, optIndex) => (
                                  <div key={optIndex} className={`p-4 rounded-lg transition-colors ${
                                    Array.isArray(question.correctAnswer)
                                      ? question.correctAnswer.includes(optIndex + 1)
                                        ? isDarkMode 
                                          ? 'bg-green-500/10 border border-green-500/20' 
                                          : 'bg-green-50 border border-green-100'
                                        : isDarkMode
                                          ? 'bg-[#1F2937] border border-gray-700'
                                          : 'bg-white border border-gray-200'
                                      : question.correctAnswer === optIndex + 1
                                        ? isDarkMode
                                          ? 'bg-green-500/10 border border-green-500/20'
                                          : 'bg-green-50 border border-green-100'
                                        : isDarkMode
                                          ? 'bg-[#1F2937] border border-gray-700'
                                          : 'bg-white border border-gray-200'
                                  }`}>
                                    <div className="flex items-start space-x-3">
                                      <span className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-sm ${
                                        Array.isArray(question.correctAnswer)
                                          ? question.correctAnswer.includes(optIndex + 1)
                                            ? isDarkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'
                                            : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
                                          : question.correctAnswer === optIndex + 1
                                            ? isDarkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600'
                                            : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
                                      }`}>
                                        {String.fromCharCode(65 + optIndex)}
                                      </span>
                                      <div className="flex-grow">
                                        <p className={`${
                                          isDarkMode ? 'text-gray-200' : 'text-gray-700'
                                        }`}>
                                          {typeof option === 'object' ? option.text : option}
                                        </p>
                                        {typeof option === 'object' && option.image && (
                                          <div className="mt-3">
                                            <img 
                                              src={option.image} 
                                              alt={`Option ${String.fromCharCode(65 + optIndex)} image`}
                                              className="max-w-full rounded-lg max-h-48 object-contain border shadow-sm" 
                                            />
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    {(Array.isArray(question.correctAnswer)
                                      ? question.correctAnswer.includes(optIndex + 1)
                                      : question.correctAnswer === optIndex + 1
                                    ) && (
                                      <div className="mt-2 flex items-center">
                                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                                          isDarkMode 
                                            ? 'bg-green-500/20 text-green-400' 
                                            : 'bg-green-100 text-green-600'
                                        }`}>
                                          Correct Answer
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className={`sticky bottom-0 px-6 py-4 flex justify-end space-x-4 border-t ${
                  isDarkMode ? 'bg-[#1a1f2e] border-gray-700' : 'bg-white border-gray-200'
                } shadow-lg`}>
                  <button
                    onClick={() => setShowReviewModal(false)}
                    className={`px-4 py-2 rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setShowReviewModal(false);
                      handleReject(selectedRequest);
                    }}
                    className={`px-4 py-2 rounded-lg ${
                      isDarkMode 
                        ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/20' 
                        : 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-100'
                    }`}
                  >
                    Reject Paper
                  </button>
                  <button
                    onClick={() => {
                      setShowReviewModal(false);
                      handleApprove(selectedRequest);
                    }}
                    className={`px-4 py-2 rounded-lg ${
                      isDarkMode 
                        ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/20' 
                        : 'bg-green-50 text-green-600 hover:bg-green-100 border border-green-100'
                    }`}
                  >
                    Approve Paper
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        )}

        {/* Add a new section for fixing exam encryption keys */}
        {/* <div className="mt-8">
          <h2 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            Maintenance Tools
          </h2>
          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow`}>
            <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Fix Exam Encryption Keys
            </h3>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              This tool fixes exams with missing or mismatched encryption keys.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={async () => {
                  try {
                    setLoading(true);
                    const response = await axios.get(
                      `${config.API_BASE_URL}/api/exams/fix-encryption-keys`,
                      { withCredentials: true }
                    );
                    showToast.success(response.data.message);
                  } catch (error) {
                    console.error('Error fixing keys:', error);
                    showToast.error('Failed to fix encryption keys');
                  } finally {
                    setLoading(false);
                  }
                }}
                className={`px-4 py-2 rounded ${
                  isDarkMode
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-blue-500 hover:bg-blue-600'
                } text-white`}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Fix All Encryption Keys'}
              </button>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default AdminDashboard;